import React, { useCallback, useMemo, useState } from "react";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import { Box, Checkbox, Dialog, DialogContent, DialogTitle, FormControlLabel, IconButton, Link, TableBody, TableContainer, TableHead } from "@material-ui/core";
import { ArrowDownward as ArrowDownwardIcon, ArrowUpward as ArrowUpwardIcon, Close as CloseIcon, ImportExportRounded as ImportExportRoundedIcon } from "@material-ui/icons";
import { nanoid } from "nanoid";
import Table from "@material-ui/core/Table";
import "./styles/GeneralTableStyles.css";
import { makeStyles } from "@material-ui/core/styles";
var useStyles = makeStyles({
    container: {
        // @ts-ignore
        maxHeight: function (props) { return props.height; },
    },
});
export var SortFilterTable = function (props) {
    var _a, _b, _c, _d;
    var entries = props.entries, columns = props.columns, onSelect = props.onSelect, isSelected = props.isSelected;
    var _e = useState((_b = (_a = props.initialSorting) === null || _a === void 0 ? void 0 : _a.order) !== null && _b !== void 0 ? _b : "asc"), order = _e[0], setOrder = _e[1];
    var _f = useState((_d = (_c = props.initialSorting) === null || _c === void 0 ? void 0 : _c.column) !== null && _d !== void 0 ? _d : null), orderBy = _f[0], setOrderBy = _f[1];
    var _g = useState(false), showDialog = _g[0], setShowDialog = _g[1];
    var _h = useState(null), currentFilterKey = _h[0], setCurrentFilterKey = _h[1];
    var _j = useState(new Map()), filters = _j[0], setFilters = _j[1];
    var columnsByKey = useMemo(function () { return (columns.reduce(function (prev, curr) {
        return (prev.set(curr.key, curr));
    }, new Map())); }, [columns]);
    var closeDialog = useCallback(function () {
        setShowDialog(false);
    }, [setShowDialog]);
    var changeSortDirection = function (key) {
        if (key === orderBy) {
            setOrder(order === "asc" ? "desc" : "asc");
        }
        else {
            setOrderBy(key);
            setOrder("asc");
        }
    };
    var onLabelClick = function (key) {
        setCurrentFilterKey(key);
        setShowDialog(true);
    };
    /* filterText used in filter summary **/
    var filterText = useCallback(function (entry, key) {
        var _a, _b;
        var getFilterLabel = (_a = columnsByKey.get(key)) === null || _a === void 0 ? void 0 : _a.getFilterText;
        var render = (_b = columnsByKey.get(key)) === null || _b === void 0 ? void 0 : _b.render;
        var label = "";
        if (getFilterLabel)
            label = getFilterLabel(entry);
        // if no explicit label function string the result of the render function
        else if (render)
            label = String(render(entry));
        return label;
    }, [columnsByKey]);
    /*
    * get all sortValues for a column
    * gives sortValues and corresponding filterTexts
    * as a mapping sortValue => filterText
    *  **/
    var sortValuesForKey = useCallback(function (key) {
        return entries.reduce(function (acc, curr) {
            var _a;
            if (key) {
                var getSortValue = (_a = columnsByKey.get(key)) === null || _a === void 0 ? void 0 : _a.getSortValue;
                var sortValue = getSortValue && getSortValue(curr);
                var text = filterText(curr, key);
                if (sortValue !== undefined && text !== "" && !acc.has(sortValue))
                    acc.set(sortValue, text);
            }
            return acc;
        }, new Map());
    }, [entries, columnsByKey, filterText]);
    var isOptionChecked = useCallback(function (key, option) {
        if (key === null)
            return false;
        var optionsForKey = filters.get(key);
        if (optionsForKey === undefined)
            return false;
        return optionsForKey.has(option);
    }, [filters]);
    /*
    * filterOptions created from all current sortValues.
    * used to apply the filtering
    * and displayed in the filter dialog
    *  **/
    var filterOptionsForKey = useCallback(function (key) {
        return Array.from(sortValuesForKey(key).entries())
            .map(function (_a) {
            var sortValue = _a[0], label = _a[1];
            return ({
                label: label,
                sortValue: sortValue,
                checked: isOptionChecked(key, sortValue)
            });
        })
            .sort(function (a, b) { return (a.sortValue < b.sortValue) ? -1 : 1; });
    }, [sortValuesForKey, isOptionChecked]);
    var filterOptionsForCurrentKey = useMemo(function () { return filterOptionsForKey(currentFilterKey); }, [currentFilterKey, filterOptionsForKey]);
    /*
    * perform the filtering
    *  **/
    var filteredEntries = entries;
    Array.from(columnsByKey.keys()).forEach(function (key) {
        var filterSet = new Set(filterOptionsForKey(key)
            .filter(function (opt) { return opt.checked; })
            .map(function (opt) { return opt.sortValue; }));
        if (filterSet.size > 0) {
            filteredEntries = filteredEntries.filter(function (entry) {
                var _a;
                var getSortValue = (_a = columnsByKey.get(key)) === null || _a === void 0 ? void 0 : _a.getSortValue;
                var showEntry = false;
                if (getSortValue) {
                    var sortValue = getSortValue(entry);
                    showEntry = filterSet.has(sortValue);
                }
                if (!showEntry && props.isSelected(entry)) {
                    if (props.deselectFilteredEntry !== undefined)
                        props.deselectFilteredEntry(entry);
                    else
                        props.onSelect(entry);
                }
                return showEntry;
            });
        }
    });
    /*
    * perform the ordering
    *  **/
    var orderedFilteredEntries = filteredEntries;
    if (orderBy !== null) {
        var entryOrder_1 = props.entryOrder;
        orderedFilteredEntries = orderedFilteredEntries.sort(function (a, b) {
            var _a;
            var getSortValue = ((_a = columnsByKey.get(orderBy)) === null || _a === void 0 ? void 0 : _a.getSortValue) || (function () { return ""; });
            var first = getSortValue(a);
            if (typeof first === "string")
                first = first.toLowerCase();
            var second = getSortValue(b);
            if (typeof second === "string")
                second = second.toLowerCase();
            var result;
            if (entryOrder_1 !== undefined && first === second)
                result = entryOrder_1(a, b);
            else {
                result = first < second ? -1 : 1;
                if (first === second)
                    result = 0;
            }
            return order === "asc" ? result : result * -1;
        });
    }
    var filterDialogTitle = useMemo(function () { var _a; return (currentFilterKey && ((_a = columnsByKey.get(currentFilterKey)) === null || _a === void 0 ? void 0 : _a.label)) || ""; }, [currentFilterKey, columnsByKey]);
    var enableFilterOptionForCurrentKey = useCallback(function (option) {
        if (currentFilterKey === null)
            return;
        if (!filters.has(currentFilterKey))
            filters.set(currentFilterKey, new Set());
        filters.get(currentFilterKey).add(option);
        setFilters(new Map(filters));
    }, [currentFilterKey, filters]);
    var disableFilterOptionForCurrentKey = useCallback(function (option) {
        if (currentFilterKey === null)
            return;
        var filterSet = filters.get(currentFilterKey);
        if (filterSet) {
            filterSet.delete(option);
            if (filterSet.size <= 0)
                filters.delete(currentFilterKey);
        }
        setFilters(new Map(filters));
    }, [currentFilterKey, filters]);
    var isFilterEnabledForCurrentKey = useMemo(function () { return filterOptionsForCurrentKey.some(function (opt) { return opt.checked; }); }, [filterOptionsForCurrentKey]);
    var disableAllFiltersForCurrentKey = useCallback(function () {
        currentFilterKey && filters.delete(currentFilterKey);
        setFilters(new Map(filters));
    }, [currentFilterKey, filters, setFilters]);
    var filterSummary = useMemo(function () {
        return Array.from(columnsByKey.values())
            .map(function (col) {
            var text = "";
            var filterOptions = filterOptionsForKey(col.key);
            if (filterOptions) {
                var opts = filterOptions
                    .filter(function (opt) { return opt.checked; })
                    .map(function (opt) { return opt.label; });
                if (opts.length > 0)
                    text += col.label + "=" + opts.join(", ");
            }
            return text;
        })
            .filter(function (text) { return text !== ""; })
            .join("; ");
    }, [columnsByKey, filterOptionsForKey]);
    var headRow = (React.createElement(TableRow, null, columns.map(function (_a) {
        var key = _a.key, label = _a.label, getSortValue = _a.getSortValue;
        var sortable = getSortValue !== undefined;
        return (React.createElement(TableCell, { key: key, style: { padding: "16px 5px" } },
            React.createElement(Box, { display: "flex", alignItems: "center", justifyContent: "center" },
                React.createElement(Box, null, sortable &&
                    React.createElement(IconButton, { color: orderBy === key ? "primary" : "secondary", style: { flex: 1, margin: 0, padding: 0 }, onClick: function () { return changeSortDirection(key); } }, (orderBy === key) ?
                        order === 'asc' ?
                            React.createElement(ArrowDownwardIcon, { "aria-disabled": "true" }) :
                            order === 'desc' ?
                                React.createElement(ArrowUpwardIcon, { "aria-disabled": "true" }) :
                                React.createElement(ImportExportRoundedIcon, { "aria-disabled": "true" })
                        : React.createElement(ImportExportRoundedIcon, { "aria-disabled": "true" }))),
                React.createElement(Box, null, sortable ? (React.createElement(Link, { onClick: function () { return onLabelClick(key); }, style: {
                        cursor: "pointer",
                        color: "rgba(0, 0, 0, 0.87)",
                        whiteSpace: "nowrap"
                    } }, label)) :
                    React.createElement("div", { style: { whiteSpace: "nowrap" } }, label)))));
    })));
    var rows = orderedFilteredEntries.map(function (entry) {
        return (React.createElement(TableRow, { key: nanoid(), onClick: function () { return onSelect(entry); }, selected: isSelected(entry) }, columns
            .map(function (_a) {
            var _b, _c;
            var key = _a.key, getCellStyle = _a.getCellStyle;
            return ({
                renderCell: ((_b = columnsByKey.get(key)) === null || _b === void 0 ? void 0 : _b.render) || (function () { return null; }),
                key: key,
                renderCellTitle: (_c = columnsByKey.get(key)) === null || _c === void 0 ? void 0 : _c.renderTitle,
                getCellStyle: getCellStyle
            });
        })
            .map(function (_a) {
            var renderCell = _a.renderCell, key = _a.key, getCellStyle = _a.getCellStyle, renderCellTitle = _a.renderCellTitle;
            var style = (getCellStyle && getCellStyle(entry)) || undefined;
            return (React.createElement(TableCell, { key: key, style: style, title: renderCellTitle ? renderCellTitle(entry) : "" }, renderCell(entry)));
        })));
    });
    var height = useMemo(function () {
        if (props.tableHeight !== undefined) {
            if (filterSummary.length > 0)
                return props.tableHeight - 43;
            else
                return props.tableHeight;
        }
        else {
            return null;
        }
    }, [props, filterSummary]);
    var fullWidth = useMemo(function () {
        if (props.fullWidth === true) {
            return true;
        }
        else {
            return false;
        }
    }, [props]);
    var classes = useStyles({ height: height });
    return (React.createElement("div", { className: "tableDiv" },
        React.createElement(FilterDialog, { show: showDialog, close: closeDialog, label: filterDialogTitle, options: filterOptionsForCurrentKey, enableFilter: enableFilterOptionForCurrentKey, disableFilter: disableFilterOptionForCurrentKey, selectAllChecked: !isFilterEnabledForCurrentKey, onSelectAll: disableAllFiltersForCurrentKey }),
        React.createElement("div", { style: { marginLeft: 20, marginRight: 20 } }, filterSummary && "Aktuell angewendete Filter: " + filterSummary),
        React.createElement(TableContainer, { className: classes.container },
            React.createElement(Table, { stickyHeader: true, style: { marginTop: 0, maxWidth: fullWidth ? "100%" : "95%" } },
                React.createElement(TableHead, null, headRow),
                React.createElement(TableBody, null, rows)))));
};
var FilterDialog = function (props) {
    var show = props.show, close = props.close, label = props.label, options = props.options, enableFilter = props.enableFilter, disableFilter = props.disableFilter, onSelectAll = props.onSelectAll, selectAllChecked = props.selectAllChecked;
    var onClose = function () {
        close();
    };
    var filterCheckBoxClicked = function (value, option) {
        if (value)
            enableFilter(option);
        else
            disableFilter(option);
    };
    return (React.createElement(Dialog, { open: show, scroll: 'paper', onClose: onClose },
        React.createElement(IconButton, { onClick: onClose, style: { position: "absolute", right: 0, top: 0 } },
            React.createElement(CloseIcon, null)),
        React.createElement(DialogTitle, { style: { paddingRight: 72 } },
            "Eintr\u00E4ge filtern nach: ",
            label),
        React.createElement(DialogContent, null,
            React.createElement(Box, null,
                React.createElement(FormControlLabel, { control: React.createElement(Checkbox, { onChange: onSelectAll, checked: selectAllChecked }), label: "alle auswählen" })),
            options.map(function (_a) {
                var label = _a.label, sortValue = _a.sortValue, checked = _a.checked;
                return (React.createElement(Box, null,
                    React.createElement(FormControlLabel, { key: sortValue, label: label, control: React.createElement(Checkbox, { onChange: function (event) { return filterCheckBoxClicked(event.target.checked, sortValue); }, checked: checked }) })));
            }))));
};
//# sourceMappingURL=SortFilterTable.js.map